import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type ReactNode } from 'react'
import type { User, UserManager } from 'oidc-client-ts'
import { createUserManager, isConfigured, readConfig, resolveUser } from './auth'
import {
  fetchAccess,
  fetchDashboard,
  fetchItCosts,
  runQuery,
  SessionExpiredError,
  triggerRefresh,
  type DashboardAccess,
  type DashboardData,
  type ItCostsData,
  type RefreshJobResult,
} from './api'
import {
  type ReloadResult,
  refreshErrorMessage,
  refreshNotice,
  shouldAutoRefresh,
} from './autoRefresh'
import { DashboardView, type DashboardTab } from './DashboardView'
import { FreshnessPill } from './components/FreshnessStatus'
import { RefreshBanner, RefreshStatusRegion } from './components/RefreshStatus'
import { ItCostsView } from './ItCostsView'
import { WindowPicker } from './components/WindowPicker'
import { createLatestOnly } from './latestOnly'
import {
  customYears,
  readWindowChoice,
  tokyoMonth,
  turnedMonth,
  windowAt,
  writeWindowChoice,
  type DataWindow,
  type WindowChoice,
} from './timeWindow'

// The finance dashboard. It shows what leadership already reads — the GOSPEL
// masthead — plus the two things the sheet could never show them: whether the
// numbers are current, and where the sheet's own pipeline double-counts.

const LOADING_MESSAGE = 'Loading the finance dashboard…'
const UPDATED_NOTE_MS = 4000

// What a load produced, tagged by the scope the signed-in user's roles allow:
// the full dashboard (finance-viewer), or the IT-costs page (finance-it-costs
// alone). The scope picks which query runs; the backend enforces it. It also
// carries the window it was fetched for: everything is drawn against THAT, never
// against the picker's newest choice, so a chart is never laid out for a window
// its numbers don't cover while a switch is still loading.
type Loaded =
  | { scope: 'full'; data: DashboardData; window: DataWindow }
  | { scope: 'it-costs'; data: ItCostsData; window: DataWindow }

export function App(): JSX.Element {
  const config = useMemo(readConfig, [])
  const configured = isConfigured(config)
  const manager = useMemo<UserManager | null>(
    () => (configured ? createUserManager(config) : null),
    [config, configured],
  )

  const [user, setUser] = useState<User | null>(null)
  const [data, setData] = useState<Loaded | null>(null)
  // The page this user's roles allow, kept apart from `data` so it survives a
  // failed load (which clears the data) and so reloads skip the access probe.
  // Cached per ACCESS TOKEN: roles live in the token, so a silent renewal that
  // carries changed roles re-asks; a failed data load drops the cache too, so a
  // refused query re-probes on the next Reload instead of repeating.
  const [access, setAccess] = useState<DashboardAccess | null>(null)
  const scope = access?.scope ?? null
  const scopeFor = useRef<{ token: string; access: DashboardAccess } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(configured)
  const [refreshing, setRefreshing] = useState(false)
  // The remembered time window. Shared by both pages, so an IT-costs user keeps
  // the choice a viewer made on the same browser.
  const [choice, setChoice] = useState<WindowChoice>(() => readWindowChoice())
  // The Tokyo month the presets resolve from. Re-checked on Reload, Refresh and
  // when the tab is shown again, so a tab left open across a month turn (or
  // 1 March) moves its window on instead of keeping the old one.
  const [month, setMonth] = useState(() => tokyoMonth(new Date()))
  const dataWindow = useMemo(() => windowAt(choice, month), [choice, month])
  // True from a window switch until its load settles; the old numbers stay on
  // screen, dimmed, meanwhile.
  const [switching, setSwitching] = useState(false)
  // Out-of-order responses: only the newest load may write state.
  const latestLoad = useRef(createLatestOnly())
  const [refreshResult, setRefreshResult] = useState<RefreshJobResult[] | null>(null)
  // When the running refresh began (drives the updating bar's timer), and the
  // brief "Updated just now" shown after a quiet, fully successful automatic one.
  const [refreshStartedAt, setRefreshStartedAt] = useState<number | null>(null)
  const [justUpdated, setJustUpdated] = useState(false)
  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mounted = useRef(true)
  // Dismissing the result banner unmounts the button that held focus; hand
  // focus back to Refresh rather than dropping it on <body>.
  const refreshButton = useRef<HTMLButtonElement | null>(null)
  // Lifted out of DashboardView so the top-bar freshness pill can open Data sources.
  const [tab, setTab] = useState<DashboardTab>('overview')

  useEffect(() => {
    if (manager === null) return
    let cancelled = false
    resolveUser(manager)
      .then((resolved) => {
        if (!cancelled) setUser(resolved)
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [manager])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      if (noteTimer.current !== null) clearTimeout(noteTimer.current)
    }
  }, [])

  const clearNote = useCallback(() => {
    if (noteTimer.current !== null) clearTimeout(noteTimer.current)
    noteTimer.current = null
    setJustUpdated(false)
  }, [])

  // Clear the now-useless session and return to the sign-in screen, rather than
  // leaving a stale "invalid token" on a dashboard the user can no longer read.
  // removeUser only clears the locally stored tokens — it does not end the
  // Zitadel SSO session — so signing back in is one click, and seamless if the
  // SSO session is still alive.
  const signOutToLogin = useCallback(
    (message: string) => {
      setData(null)
      setAccess(null)
      scopeFor.current = null
      setRefreshResult(null)
      clearNote()
      setError(message)
      if (manager === null) {
        setUser(null)
        return
      }
      void manager.removeUser().finally(() => setUser(null))
    },
    [manager, clearNote],
  )

  // Keep the app on the freshest token and off the login screen when it doesn't
  // need to be there. automaticSilentRenew refreshes the token near expiry but
  // only updates the manager's store — adopt the renewed user so requests use
  // the new token (this is what stops an active session getting bounced when the
  // old token lapses).
  useEffect(() => {
    if (manager === null) return
    const onRenewed = (renewed: User): void => setUser(renewed)
    const onExpired = (): void => signOutToLogin('Your session expired. Please sign in again.')
    manager.events.addUserLoaded(onRenewed)
    // Sign out only when the token has ACTUALLY expired — deliberately NOT on
    // silentRenewError, which oidc fires on a transient renewal blip (~60s
    // before expiry) while the current token is still valid; bouncing the user
    // then would discard an in-progress edit for nothing. If renewal keeps
    // failing, accessTokenExpired fires at real expiry and signs out here, and a
    // request that races it is still caught as SessionExpiredError.
    manager.events.addAccessTokenExpired(onExpired)
    return () => {
      manager.events.removeUserLoaded(onRenewed)
      manager.events.removeAccessTokenExpired(onExpired)
    }
  }, [manager, signOutToLogin])

  const load = useCallback(
    async (signedIn: User, window: DataWindow): Promise<ReloadResult> => {
      const ticket = latestLoad.current.next()
      const superseded = (): boolean => !latestLoad.current.isLatest(ticket)
      setError(null)
      try {
        // Ask which page this user gets BEFORE fetching it: a scoped user's
        // token is refused by every field of the full dashboard query.
        const token = signedIn.access_token
        let known = scopeFor.current?.token === token ? scopeFor.current.access : null
        if (known === null) {
          known = await fetchAccess(config.apiUrl, token)
          scopeFor.current = { token, access: known }
        }
        if (superseded()) return 'loaded'
        setAccess(known)
        const loaded: Loaded =
          known.scope === 'full'
            ? {
                scope: 'full',
                data: await fetchDashboard(config.apiUrl, token, window.from, window.to),
                window,
              }
            : {
                scope: 'it-costs',
                data: await fetchItCosts(config.apiUrl, token, window.from, window.to),
                window,
              }
        if (superseded()) return 'loaded'
        setData(loaded)
        return 'loaded'
      } catch (cause) {
        // An expired/rejected session is not a data error to display — sign out
        // and show the login page so the user can simply sign back in.
        if (cause instanceof SessionExpiredError) {
          signOutToLogin(cause.message)
          return 'signed-out'
        }
        // A newer load owns the screen now; this one's failure is not its to show.
        if (superseded()) return 'loaded'
        scopeFor.current = null
        // Other errors replace the data rather than sitting beside it: a stale
        // table under a red banner is exactly the "looks authoritative, isn't"
        // failure this project exists to remove.
        setData(null)
        setError(cause instanceof Error ? cause.message : String(cause))
        return 'failed'
      } finally {
        if (!superseded()) setSwitching(false)
      }
    },
    [config.apiUrl, signOutToLogin],
  )

  // One effect for sign-in AND a window switch, so mount fetches once.
  useEffect(() => {
    if (user === null) return
    void load(user, dataWindow)
  }, [user, dataWindow, load])

  // Back on a tab left open across a month turn: move a preset window on, and
  // the effect above loads it.
  useEffect(() => {
    const onShow = () => {
      if (document.visibilityState !== 'visible') return
      setMonth((shown) => turnedMonth(choice, shown, new Date()) ?? shown)
    }
    document.addEventListener('visibilitychange', onShow)
    return () => document.removeEventListener('visibilitychange', onShow)
  }, [choice])

  // Trigger a live ingest of all three sources, then re-read. The per-job
  // outcome is kept and shown: a refresh where payroll failed must not look
  // like a clean one just because the deal sync landed. An automatic refresh
  // (on open) is quiet when everything lands and loud when anything fails; a
  // manual click always shows the result.
  const refresh = useCallback(
    async (signedIn: User, mode: 'manual' | 'auto') => {
      clearNote()
      const startedAt = Date.now()
      setRefreshing(true)
      setRefreshStartedAt(startedAt)
      setRefreshResult(null)
      // A month turn moves the window first, so the reload below reads the one
      // the page is about to show. The window effect also reads it straight
      // away; this reload is newer, so it wins.
      const turned = turnedMonth(choice, month, new Date())
      if (turned !== null) setMonth(turned)
      const readWindow = turned === null ? dataWindow : windowAt(choice, turned)
      try {
        const outcome = await triggerRefresh(config.apiUrl, signedIn.access_token)
        const notice = refreshNotice(mode, outcome, await load(signedIn, readWindow))
        // After the reload, so the result never shows beside the updating bar.
        if (notice === 'banner') setRefreshResult(outcome)
        if (notice === 'updated-note' && mounted.current) {
          setJustUpdated(true)
          noteTimer.current = setTimeout(() => setJustUpdated(false), UPDATED_NOTE_MS)
        }
      } catch (cause) {
        if (cause instanceof SessionExpiredError) {
          signOutToLogin(cause.message)
          return
        }
        setError(refreshErrorMessage(cause, Date.now() - startedAt, data !== null))
      } finally {
        if (mounted.current) {
          setRefreshing(false)
          setRefreshStartedAt(null)
        }
      }
    },
    [config.apiUrl, data, choice, month, dataWindow, load, signOutToLogin, clearNote],
  )

  // Once per page load, after the first successful full-scope load, pull fresh
  // data if any source is old. The ref is set BEFORE the call: StrictMode runs
  // effects twice in dev, refresh's own reload sets `data` again, and a silent
  // token renewal changes `user`; none of those may start a second refresh. A
  // Reload click or an IT-costs user never reaches the call.
  const autoChecked = useRef(false)
  useEffect(() => {
    if (autoChecked.current || user === null || data?.scope !== 'full') return
    autoChecked.current = true
    if (shouldAutoRefresh(data.data.sourceFreshness, Date.now())) void refresh(user, 'auto')
  }, [data, user, refresh])

  if (!configured) {
    return (
      <Shell>
        <h1>Finance dashboard</h1>
        <p className="notice">
          Not configured yet. This build has no Zitadel issuer, client id or API URL, which means
          the OIDC app has not been created (or terraform has not been applied with it). A sign-in
          button here could not work, so there isn’t one.
        </p>
      </Shell>
    )
  }

  if (loading) {
    return (
      <Shell>
        {/* Same copy as the in-flight-read spinner below, so completing sign-in
            and then loading the data read as one continuous load, not two. */}
        <LoadingPanel message={LOADING_MESSAGE} />
      </Shell>
    )
  }

  if (user === null) {
    return (
      <Shell>
        <h1>ZooKeep Finance</h1>
        <p>Sign in with your ZooKeep account to view the finance masthead.</p>
        {error !== null && <p className="error">{error}</p>}
        <button type="button" onClick={() => void manager?.signinRedirect()}>
          Sign in with Zitadel
        </button>
      </Shell>
    )
  }

  const dataEnd = data?.scope === 'full' ? (data.data.gospelCashFlow.at(-1)?.month ?? null) : null

  return (
    <Shell>
      <header className="topbar">
        <h1>ZooKeep Finance</h1>
        <div className="who">
          {data?.scope === 'full' && (
            <FreshnessPill freshness={data.data.sourceFreshness} onOpen={() => setTab('sources')} />
          )}
          <span>{user.profile.email ?? user.profile.name ?? user.profile.sub}</span>
          {/* Refresh runs every ingest (finance-viewer only): shown only once the
              scope is known to be full, never to an IT-costs user, whose token
              the backend would refuse. */}
          {scope === 'full' && (
            <button
              type="button"
              className="primary"
              ref={refreshButton}
              onClick={() => void refresh(user, 'manual')}
              disabled={refreshing}
              title="Pull fresh data now (deals, payroll, forecast, freee costs), then reload."
            >
              {refreshing ? 'Refreshing…' : 'Refresh data'}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setRefreshResult(null)
              // A turned month moves the window, and the window effect loads it.
              const turned = turnedMonth(choice, month, new Date())
              if (turned !== null) setMonth(turned)
              else void load(user, dataWindow)
            }}
            disabled={refreshing}
          >
            Reload
          </button>
          <button type="button" onClick={() => void manager?.signoutRedirect()}>
            Sign out
          </button>
        </div>
      </header>

      <RefreshStatusRegion startedAt={refreshStartedAt} justUpdated={justUpdated} />
      {refreshResult !== null && (
        <RefreshBanner
          results={refreshResult}
          onDismiss={() => {
            setRefreshResult(null)
            refreshButton.current?.focus()
          }}
        />
      )}

      {error !== null && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {/* Shown once the page is known, not only while data is: a switch whose
          load fails clears the data, and the reader must still be able to
          switch back. With no data, the range shown is the chosen window. */}
      {access !== null && (
        <WindowPicker
          window={data?.window ?? dataWindow}
          checked={choice.kind}
          // A no-op returns early: nothing would reload, so "switching" would stick on.
          onChange={(kind) => {
            if (kind === choice.kind) return
            const next: WindowChoice = { ...choice, kind }
            writeWindowChoice(next)
            setChoice(next)
            // A custom range never re-checks the month, so it may be stale.
            setMonth(tokyoMonth(new Date()))
            setSwitching(true)
          }}
          onApplyCustom={(range) => {
            if (
              choice.kind === 'custom' &&
              choice.custom?.from === range.from &&
              choice.custom.to === range.to
            ) {
              return
            }
            const next: WindowChoice = { kind: 'custom', custom: range }
            writeWindowChoice(next)
            setChoice(next)
            setSwitching(true)
          }}
          custom={choice.custom}
          years={customYears(dataEnd, new Date())}
          disabled={refreshing}
          dataEnd={dataEnd}
        />
      )}

      <div
        className={refreshing || switching ? 'dashboard-body is-updating' : 'dashboard-body'}
        aria-busy={refreshing || switching}
      >
        {data?.scope === 'full' ? (
          <DashboardView
            data={data.data}
            window={data.window}
            tab={tab}
            onTabChange={setTab}
            apiUrl={config.apiUrl}
            token={user.access_token}
            onPublished={() => void load(user, dataWindow)}
            onSessionExpired={signOutToLogin}
            runQuery={
              access?.canQuery === true
                ? (statement) => runQuery(config.apiUrl, user.access_token, statement)
                : undefined
            }
          />
        ) : data?.scope === 'it-costs' ? (
          <ItCostsView
            data={data.data}
            apiUrl={config.apiUrl}
            token={user.access_token}
            onSaved={() => void load(user, dataWindow)}
            onSessionExpired={signOutToLogin}
          />
        ) : (
          // First load after sign-in has no data yet. Show a spinner while the
          // automatic read runs, so the page never reads as blank/broken — the
          // blank was sending people to the heavier "Refresh data" button. A
          // failed load sets `error` (shown above) and clears this branch instead.
          error === null && <LoadingPanel message={LOADING_MESSAGE} />
        )}
      </div>
    </Shell>
  )
}

function Shell({ children }: { children: ReactNode }): JSX.Element {
  return <main className="shell">{children}</main>
}

// A centred spinner + message, shown while the session resolves and while the
// dashboard's first read is in flight. role=status/aria-live announces it to a
// screen reader; the spinner itself is decorative.
function LoadingPanel({ message }: { message: string }): JSX.Element {
  return (
    <div className="loading-panel" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      <span>{message}</span>
    </div>
  )
}
